import { useMemo, useState } from 'react';
import { HelpCircle, MessageSquareText, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useStore } from '../../store/useStore';
import type { WeekSnapshot } from '../../engine/notebook';
import { coreTagIds, recordsById, reviewWeek, weeksNewestFirst } from '../../engine/insights';
import { weeklyInsightsEnabled } from '../../native/insightsGate';
import { FocusPicker } from './FocusPicker';
import { publicUrl } from '../../native/native';

/**
 * "Your week" review (Weekly Insights feature 1, spec §2). Up to three
 * facts, one question, one suggestion — computed on the device from the
 * week's frozen snapshot, the earlier weeks, the LIVE ★ flags and the
 * Mastery record; no AI, no network, nothing stored. Web only (D5):
 * renders nothing when the gate is closed.
 *
 * Feedback (spec §6): 👍 / 👎 + an optional line, signed-in users only.
 * The vote is remembered on the device so the card never nags.
 */
export function WeekReviewCard({ snap, title = 'Your week' }: { snap: WeekSnapshot; title?: string }) {
  const boundWeeks = useStore((s) => s.boundWeeks);
  const tags = useStore((s) => s.tags);
  const weekHistory = useStore((s) => s.weekHistory);
  const syncEmail = useStore((s) => s.syncEmail);
  const given = useStore((s) => s.insightFeedbackGiven[snap.id]);
  const rate = useStore((s) => s.rateWeekReview);
  const [commenting, setCommenting] = useState<'up' | 'down' | null>(null);
  const [comment, setComment] = useState('');

  const review = useMemo(() => {
    const newestFirst = weeksNewestFirst(boundWeeks);
    const earlier = newestFirst.filter((w) => w.endedAt < snap.endedAt);
    return reviewWeek(snap, earlier, coreTagIds(tags), recordsById(weekHistory));
  }, [boundWeeks, snap, tags, weekHistory]);

  if (!weeklyInsightsEnabled()) return null;

  const submit = (useful: 'up' | 'down') => {
    rate(snap.id, useful, comment);
    setCommenting(null);
    setComment('');
  };

  return (
    <section
      aria-label={`${title}: ${snap.label}`}
      className="space-y-3 rounded-2xl border border-brand-200 dark:border-brand-500/25 bg-gradient-to-br from-brand-50 dark:from-brand-950/30 to-white dark:to-slate-900 p-4 shadow-sm"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-black text-slate-900 dark:text-white">{title}</h3>
        <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
          {snap.label}
          <a
            href={publicUrl('/guide.html#weekly-review')}
            target="_blank"
            rel="noreferrer"
            aria-label="How the weekly review, focus projects and hand-offs work"
            title="How the weekly review, focus projects and hand-offs work"
            className="rounded-full p-0.5 text-slate-400 transition-colors hover:text-brand-600 dark:hover:text-brand-400"
          >
            <HelpCircle size={13} />
          </a>
        </span>
      </div>

      <ul className="space-y-1.5">
        {review.facts.map((f) => (
          <li key={f.id} className="flex gap-2 text-xs leading-relaxed text-slate-800 dark:text-slate-200">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" aria-hidden />
            <span>{f.text}</span>
          </li>
        ))}
      </ul>

      {review.question && (
        <p className="text-xs font-semibold text-slate-900 dark:text-white">{review.question}</p>
      )}
      {review.suggestion && (
        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">{review.suggestion}</p>
      )}

      <FocusPicker />

      {syncEmail && !review.thin && (
        <div className="space-y-2 border-t border-brand-100 dark:border-brand-500/15 pt-2">
          {given ? (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Thanks — you marked this review {given === 'up' ? 'useful' : 'not useful'}.
            </p>
          ) : commenting ? (
            <div className="space-y-1.5">
              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300" htmlFor={`review-comment-${snap.id}`}>
                Anything to add? (optional)
              </label>
              <input
                id={`review-comment-${snap.id}`}
                type="text"
                value={comment}
                maxLength={200}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit(commenting);
                  if (e.key === 'Escape') setCommenting(null);
                }}
                placeholder={commenting === 'down' ? 'What was off or wrong?' : 'What landed?'}
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-base sm:text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-brand-500 focus:outline-none"
              />
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => submit(commenting)}
                  className="rounded-md bg-brand-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-brand-500"
                >
                  Send
                </button>
                <button
                  onClick={() => setCommenting(null)}
                  className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Useful?</span>
              <button
                onClick={() => setCommenting('up')}
                aria-label="Useful"
                className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              >
                <ThumbsUp size={12} /> Yes
              </button>
              <button
                onClick={() => setCommenting('down')}
                aria-label="Not useful"
                className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ThumbsDown size={12} /> No
              </button>
              <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                <MessageSquareText size={11} /> A wrong number? Say so — it gets fixed.
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
